import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Circle, Key, Download, Zap, Rocket, X } from 'lucide-react';

/**
 * GetStartedChecklist — shows on Dashboard until user has first trade sync.
 * Steps: Sign up → Get API Key → Attach EA → Data flows
 * Dismissible via localStorage.
 */
export default function GetStartedChecklist({ hasAccounts = false, hasTrades = false }) {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('onboarding_dismissed') === '1'
  );

  // Don't show if dismissed or if they have trades (onboarding complete)
  if (dismissed || hasTrades) return null;

  const steps = [
    { key: 'signup', label: 'Create your account', done: true, icon: CheckCircle },
    { key: 'apikey', label: 'Generate an API key', done: hasAccounts, icon: Key, link: '/settings/api-keys' },
    { key: 'ea', label: 'Attach TrackMyFxEA to a chart', done: hasAccounts, icon: Download, link: '/settings/ea-download' },
    { key: 'sync', label: 'First data sync received', done: hasTrades, icon: Zap },
  ];

  const completedCount = steps.filter(s => s.done).length;
  const progress = (completedCount / steps.length) * 100;

  const handleDismiss = () => {
    localStorage.setItem('onboarding_dismissed', '1');
    setDismissed(true);
  };

  return (
    <div className="bg-gradient-to-br from-emerald-500/5 to-teal-500/5 border border-emerald-500/20 rounded-xl p-5 mb-6 relative">
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-gray-500 hover:text-gray-300 p-1"
        aria-label="Dismiss onboarding"
      >
        <X size={16} />
      </button>

      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
          <Rocket size={20} className="text-emerald-400" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-200">Get Started</h3>
          <p className="text-xs text-gray-500">{completedCount}/{steps.length} steps completed</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-dark-surface rounded-full mb-4 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Steps */}
      <div className="space-y-2.5">
        {steps.map((step) => {
          const StepIcon = step.done ? CheckCircle : Circle;
          const content = (
            <div className={`flex items-center gap-3 px-3 py-2 rounded-lg ${step.done ? 'opacity-60' : 'hover:bg-white/[0.03]'}`}>
              <StepIcon size={16} className={step.done ? 'text-emerald-400' : 'text-gray-600'} />
              <span className={`text-sm ${step.done ? 'text-gray-500 line-through' : 'text-gray-300'}`}>{step.label}</span>
              {!step.done && step.link && (
                <span className="ml-auto text-xs text-emerald-400">→</span>
              )}
            </div>
          );

          if (!step.done && step.link) {
            return <Link key={step.key} to={step.link}>{content}</Link>;
          }
          return <div key={step.key}>{content}</div>;
        })}
      </div>
    </div>
  );
}
